import { useCallback, useMemo, useState } from "react";
import { Form, Select } from "antd";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import type { FormListFieldData, FormListOperation } from "antd/lib/form/FormList";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { isSubstring } from "@/shared/utils/comparison/comparison";
import { ReactComponent as CheckedSVG } from "@/assets/icons/checked.svg";
import { useGetAllTestsHook } from "@/modules/admin/testManagement/hooks/useGetAllTests.hook";
import { TestItem } from "@/shared/components/assessment/new/TestItem/TestItem";
import { getTestDisplayName } from "@/shared/services/tests/tests.utils";
import type { TemplateTestDetails } from "./useTemplateTestDetails";
import type { TemplateFormData } from "./types";
import style from "./style.module.css";

interface TestSelectorProps {
  id: string;
  fields: FormListFieldData[];
  addTestToList: FormListOperation["add"];
  removeTestsFromList: FormListOperation["remove"];
  moveTestInList: FormListOperation["move"];
  hasListError: boolean;
  testDetails: TemplateTestDetails;
}

export function TestSelector({
  id,
  fields,
  addTestToList,
  removeTestsFromList,
  moveTestInList,
  hasListError,
  testDetails,
}: TestSelectorProps) {
  const form = Form.useFormInstance<TemplateFormData>();
  const { tests, isLoading: isLoadingAllTests } = useGetAllTestsHook();
  const { details, loadingIds: loadingTestIds, fetchTest } = testDetails;
  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);

  const orderedTests = useMemo(
    () => [...tests].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [tests],
  );
  const selectedTests = Form.useWatch("tests", form);
  const selectedTestIds = useMemo(() => (selectedTests ?? []).map((test) => test.testId), [selectedTests]);

  const handleSelectChange = useCallback(
    (newTestIds: number[]) => {
      const newTestIdsSet = new Set(newTestIds);
      const previousTestIds = new Set(selectedTestIds);

      const indicesToRemove = selectedTestIds.flatMap((id, i) => (newTestIdsSet.has(id) ? [] : [i]));
      if (indicesToRemove.length > 0) removeTestsFromList(indicesToRemove);

      newTestIds
        .filter((testId) => !previousTestIds.has(testId))
        .forEach((testId) => {
          addTestToList({ testId });
          fetchTest(testId);
        });
    },
    [selectedTestIds, addTestToList, removeTestsFromList, fetchTest],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      moveTestInList(result.source.index, result.destination.index);
    },
    [moveTestInList],
  );

  return (
    <>
      <div>
        <Select
          id={id}
          mode="multiple"
          status={hasListError ? "error" : undefined}
          value={selectedTestIds}
          showArrow={true}
          showSearch={true}
          placeholder={isSelectorOpen ? "" : "Select Test(s)"}
          maxTagPlaceholder={isSelectorOpen ? "" : "Select Test(s)"}
          maxTagCount={0}
          onDropdownVisibleChange={(isOpen) => setIsSelectorOpen(isOpen)}
          onBlur={() => setIsSelectorOpen(false)}
          menuItemSelectedIcon={<CheckedSVG />}
          options={orderedTests.map((test) => ({
            value: test.testId,
            label: getTestDisplayName(test),
          }))}
          optionFilterProp="label"
          filterOption={(input, option) => isSubstring(String(option?.label), input)}
          onChange={(newIds) => handleSelectChange(newIds)}
          loading={isLoadingAllTests}
          dropdownMatchSelectWidth={false}
          className={style.testsDropdown}
        />
      </div>
      {fields.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="testsList">
            {(provided) => (
              <div className={style.testsList} {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((field, index) => {
                  const testId = selectedTestIds[index];
                  if (!testId) return null;

                  const isRowLoading = loadingTestIds.has(testId);
                  const rowDetails = details[testId];
                  const hasFailedToLoad = !isRowLoading && !rowDetails;

                  const normId = selectedTests?.[index]?.normId;
                  const hasNormError = rowDetails !== undefined && (rowDetails.norms.length === 0 || !normId);

                  return (
                    <Draggable key={field.key} draggableId={String(field.key)} index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          {hasFailedToLoad ? (
                            <div className={style.failedRow}>
                              <p>There was an issue loading this test. Please remove it and try again.</p>
                              <Button
                                colorType={ButtonColorType.transparentDark}
                                buttonSize={ButtonSize.XSmall}
                                onClick={() => removeTestsFromList(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <Form.Item
                              name={[index, "normId"]}
                              valuePropName="normId"
                              trigger="onNormChange"
                              noStyle={true}
                            >
                              <TestItem
                                id={testId}
                                name={rowDetails?.test.name ?? ""}
                                internalName={rowDetails?.test.internalName}
                                elementList={rowDetails?.test.testElements ?? []}
                                onDeleteClick={() => removeTestsFromList(index)}
                                isChangeable={true}
                                canExpandElements={true}
                                norms={rowDetails?.norms}
                                isLoading={isRowLoading}
                                hasNormError={hasNormError}
                              />
                            </Form.Item>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  );
}
